import React from "react";
import SideNav from "@hid-galaxy-ui/galaxy-react/components/SideNav";
import { Outlet, useLocation, useNavigate } from "react-router";
import { useAppDispatch, useAppSelector } from "../../../redux";
import { useMediaQuery } from "@mui/material";
import {
  Iconography,
  IconographySizes,
  Notification,
  NotificationVariants,
} from "@hid-galaxy-ui/galaxy-react";
import BottomNavigationMenu from "../../../common/components/BottomNavigationMenu/BottomNavigationMenu";
import { cartSliceAction } from "../../../redux/slices/cart";
import { usePartnerSessionCanAccessProjects } from "../../../hooks/usePartnerSessionPermissions";
import { useServiceCatalogPermissions } from "../../../hooks/useServiceCatalogPermissions";
import {
  useIsPartnerProgramEligible,
  usePartnerProgramBaseEligible,
} from "../../../hooks/useIsPartnerProgramEligible";
import { getConsentStatusSagaAction } from "../../../redux/saga/partner-program/saga-actions";
import { partnerTierSliceAction } from "../../../redux/slices/partner-program";
import "./PartnerDashboard.scss";

const PartnerDashboard = () => {
  const [toasts, setToasts] = React.useState([]);
  const { addToCartMessage } = useAppSelector((state) => state.cart);
  const { partnerDetails } = useAppSelector((state) => state.partner_details);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const pathName = location.pathname;
  const pathNameArray = pathName.split("/");
  const selectedNavKey = pathNameArray.includes("partner-program")
    ? "partner-program"
    : pathNameArray[pathNameArray.length - 1];
  const isMobile = useMediaQuery("(max-width:1023px)");
  const partnerProgramAccess = useIsPartnerProgramEligible();
  const partnerProgramBaseEligible = usePartnerProgramBaseEligible();
  const projectsAccess = usePartnerSessionCanAccessProjects();
  const { canAccess: servicesAccess } = useServiceCatalogPermissions();
  const { consentChecked, consentLoading } = useAppSelector(
    (state) => state.partner_program
  );
  const partnerProgramAccessResolved =
    Boolean(partnerDetails) &&
    (!partnerProgramBaseEligible || (consentChecked && !consentLoading));
  const previousPartnerIdRef = React.useRef<number | undefined>(undefined);

  const navItems = React.useMemo(
    () =>
      [
        {
          iconName: "home",
          key: "home",
          title: "Home",
        },
        ...(servicesAccess
          ? [
              {
                iconName: "cartShopping",
                key: "services",
                title: "Services",
              },
            ]
          : []),
        ...(projectsAccess
          ? [
              {
                iconName: "folderBlank",
                key: "projects",
                title: "Projects",
              },
            ]
          : []),
        ...(partnerProgramAccess
          ? [
              {
                iconName: "awardSimple",
                key: "partner-program",
                title: "Partner Program",
              },
            ]
          : []),
        {
          iconName: "gear",
          key: "settings",
          title: "Settings",
        },
      ] as { iconName: string; key: string; title: string }[],
    [partnerProgramAccess, projectsAccess, servicesAccess]
  );

  React.useEffect(() => {
    window.scrollTo({ top: 0 });
    return () => {
      dispatch(
        cartSliceAction.setAddToCartMessage({
          success: true,
          message: "",
        })
      );
    };
  }, [dispatch]);

  React.useEffect(() => {
    const id = partnerDetails?.ID;
    if (id == null) return;

    if (
      previousPartnerIdRef.current !== undefined &&
      previousPartnerIdRef.current !== id
    ) {
      dispatch(partnerTierSliceAction.setConsentStatus(null));
      dispatch(partnerTierSliceAction.setConsentChecked(false));
      dispatch(partnerTierSliceAction.setPartnerProgramConsentEligible(null));
    }
    previousPartnerIdRef.current = id;
  }, [dispatch, partnerDetails?.ID]);

  React.useEffect(() => {
    if (partnerDetails?.ID && partnerProgramBaseEligible) {
      dispatch(getConsentStatusSagaAction(partnerDetails.ID));
    }
  }, [dispatch, partnerDetails?.ID, partnerProgramBaseEligible]);

  React.useEffect(() => {
    const urlRegex = /^\/partner\/\d+\/dashboard\/?$/;
    if (urlRegex.test(pathName)) navigate("home");
    const element = document.getElementById(
      pathNameArray[pathNameArray.length - 1]
    );
    element?.click();
  }, [pathName, navigate]);

  React.useEffect(() => {
    if (
      partnerProgramAccessResolved &&
      !partnerProgramAccess &&
      pathNameArray.includes("partner-program")
    ) {
      navigate("home", { replace: true });
    }
  }, [partnerProgramAccessResolved, partnerProgramAccess, pathNameArray, navigate]);

  React.useEffect(() => {
    if (addToCartMessage.message.length > 0) {
      let content = {};
      if (addToCartMessage.success) {
        content = {
          id: Math.floor(Math.random() * 1000),
          title: "",
          message: addToCartMessage.message,
          variant: NotificationVariants.Success,
          icon: (
            <Iconography icon="circleCheck" size={IconographySizes.Medium} />
          ),
        };
      } else {
        content = {
          id: Math.floor(Math.random() * 1000),
          title: "",
          message: addToCartMessage.message,
          variant: NotificationVariants.Error,
          icon: <Iconography icon="ban" size={IconographySizes.Medium} />,
        };
      }
      setToasts((prev) => [...prev, content]);
    }
  }, [addToCartMessage]);

  const onNodeClick = (_e: unknown, key: string) => {
    navigate(key);
  };

  const mobilePrimaryKeys = React.useMemo(
    () => new Set(["home", "services", "settings"] as const),
    []
  );

  const mobileMenuData = React.useMemo(
    () => navItems.filter((item) => mobilePrimaryKeys.has(item.key as any)),
    [navItems, mobilePrimaryKeys]
  );

  const mobileMoreMenuData = React.useMemo(
    () => navItems.filter((item) => !mobilePrimaryKeys.has(item.key as any)),
    [navItems, mobilePrimaryKeys]
  );

  return (
    <div className="partner-dashboard-container">
      <Notification toasts={toasts} setToasts={setToasts} />
      {isMobile ? (
        <BottomNavigationMenu
          menuData={mobileMenuData}
          moreMenuDataEnabled={mobileMoreMenuData.length > 0}
          moreMenuData={mobileMoreMenuData}
        />
      ) : (
        <SideNav
          id="sidenav"
          mode="Colapsed"
          selected={selectedNavKey}
          navItems={navItems}
          onNodeClick={(e, key) => onNodeClick(e, key)}
        />
      )}
      <div className="outlet-view">
        <Outlet />
      </div>
    </div>
  );
};

export default PartnerDashboard;
